import { useEffect, useMemo, useRef, useState } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Plus, Search, ChevronRight, Beef, Scale, Camera, Syringe, Radio, Baby,
  User, Landmark, X, Tag, List, LayoutGrid, CalendarDays, Star
} from 'lucide-react'
import BindingChip, { BINDING_STATUS } from '../../components/SmartKraalBindingChip'
import DeltaBadge from '../../components/WeightDelta'
import MediaThumb from '../../components/MediaThumb'
import PresenceChip from '../../components/PresenceChip'
import StarButton from '../../components/StarButton'
import { MAX_STARRED_IDS, useStarred } from '../../utils/stars'
import {
  getCattle, getHerdSummary, getSmartKraalRegistrationCounts, getSmartKraalRegistrationMine
} from '../../services/api'
import RfidScanModal from '../../components/RfidScanModal'
import PageHeader from '../../components/PageHeader'
import { useAuth } from '../../contexts/AuthContext'
import { variantUrl } from '../../utils/media'
import { formatDay, formatKg, weightDelta } from '../../utils/presence'

// Category display order + colors. Order is roughly females → young → males
// so the composition bar reads as a herd structure. Every category the herd
// actually contains is rendered, so the legend always sums to the total
// (no more "the numbers don't add up" impression).
const CATEGORY_META = [
  { key: 'cow',    label: 'Cows',    bar: 'bg-purple-500', dot: 'bg-purple-500' },
  { key: 'heifer', label: 'Heifers', bar: 'bg-pink-500',   dot: 'bg-pink-500'   },
  { key: 'weaner', label: 'Weaners', bar: 'bg-orange-500', dot: 'bg-orange-500' },
  { key: 'calf',   label: 'Calves',  bar: 'bg-amber-400',  dot: 'bg-amber-400'  },
  { key: 'bull',   label: 'Bulls',   bar: 'bg-blue-500',   dot: 'bg-blue-500'   },
  { key: 'steer',  label: 'Steers',  bar: 'bg-green-500',  dot: 'bg-green-500'  },
  { key: 'oxen',   label: 'Oxen',    bar: 'bg-gray-500',   dot: 'bg-gray-500'   },
]

// Kraal presence filters (GET /cattle ?seen=, plan smartkraal-owner-experience A2/B1).
// In the query string like the binding filter, so the dashboard, the back button
// and a shared link all land on the same list.
const SEEN_FILTERS = [
  { key: 'last_dip', label: 'At the last dip', title: 'Read on the last dip day (the last day the kraal read the herd)' },
  { key: '7d', label: 'Seen in 7 days', title: 'Read by the kraal in the last 7 days' },
  { key: '30d', label: 'Seen in 30 days', title: 'Read by the kraal in the last 30 days' },
  { key: 'unseen', label: 'Not seen since the last dip', title: 'Seen before, but not on or after the last dip day' },
  { key: 'never', label: 'Never at the kraal', title: 'A bound tag the kraal has never read' },
]

// List | Wall (plan B1). The choice is remembered on this device; an owner
// starts on the wall (their animals' faces), everyone else on the list.
const VIEW_KEY = 'cattle.view'
const PAGE = 60
const ownerScopeClearedKey = (ranchId) => `cattle.ownerScopeCleared.${ranchId}`

// What a row or tile prints for weight: the kraal's latest weighing with its
// change against the one before (both from the sightings) when the kraal has
// weighed the animal, else the weight of record alone: a manual weighing has no
// kraal delta to pair with, so no arrow.
function weightCell(animal) {
  if (animal.kraal_weight_kg != null) {
    return {
      kg: animal.kraal_weight_kg, delta: weightDelta(animal.weight_delta_kg),
      title: `At the kraal${animal.kraal_weighed_on ? ` on ${formatDay(animal.kraal_weighed_on)}` : ''}`,
    }
  }
  if (animal.last_weight_kg != null) return { kg: animal.last_weight_kg, delta: null, title: 'Weight of record' }
  return null
}

export default function CattleList({ ranchId }) {
  const navigate = useNavigate()
  const { canWrite, role } = useAuth()
  const isOwner = role === 'owner'
  const [searchTerm, setSearchTerm] = useState('')
  // Deep links from the owner's registration card (Phase 4a): these filters
  // live in the query string (the backend reads the same names), so the card's
  // pills, the back button and a shared link all land on the same list.
  const [searchParams, setSearchParams] = useSearchParams()
  const ownerSegmentId = searchParams.get('owner_segment_id') || ''
  const skStatus = searchParams.get('smartkraal_status') || ''
  const seen = searchParams.get('seen') || ''
  const setSkStatus = (key) => {
    const next = new URLSearchParams(searchParams)
    if (!key || key === skStatus) next.delete('smartkraal_status')
    else next.set('smartkraal_status', key)
    setSearchParams(next, { replace: true })
  }
  const setSeen = (key) => {
    const next = new URLSearchParams(searchParams)
    if (!key || key === seen) next.delete('seen')
    else next.set('seen', key)
    setSearchParams(next, { replace: true })
  }
  // Starred animals (plan B2): the stars live on this device (utils/stars.js);
  // the filter is in the URL like the others and sends the ids to the API, so
  // the count, the pages and both views stay the server's.
  const starred = searchParams.get('starred') === '1'
  const starredIds = useStarred(ranchId)
  const toggleStarred = () => {
    const next = new URLSearchParams(searchParams)
    if (starred) next.delete('starred')
    else next.set('starred', '1')
    setSearchParams(next, { replace: true })
  }
  const starredEmpty = starred && starredIds.length === 0
  const clearOwnerSegment = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('owner_segment_id')
    setSearchParams(next, { replace: true })
    // the owner chose the whole herd: do not pre-select their segment again this session
    try { sessionStorage.setItem(ownerScopeClearedKey(ranchId), '1') } catch { /* private mode */ }
  }
  // SmartKraal registration badge: tags the kraal has seen that no animal
  // wears yet. configured:false on ranches without the feed (button hidden).
  const { data: regCounts, isPending: regPending } = useQuery({
    queryKey: ['smartkraalRegCounts', ranchId],
    queryFn: ({ signal }) => getSmartKraalRegistrationCounts(ranchId, { signal }),
    enabled: !!ranchId,
    staleTime: 5 * 60 * 1000,
  })
  // A linked owner's segment is pre-selected (plan B1): the same scoping the
  // dashboard's registration card uses, applied once per session until the owner
  // clears it. One segment only; an owner linked to several picks by hand. The
  // scope is derived here, synchronously, so the first list request already
  // carries it (no unscoped page fetched and thrown away); the effect below only
  // mirrors it into the URL, where the chip, the back button and links read it.
  const { data: mine, isPending: minePending } = useQuery({
    queryKey: ['smartkraalRegMine', ranchId],
    queryFn: ({ signal }) => getSmartKraalRegistrationMine(ranchId, { signal }),
    enabled: !!ranchId && isOwner,
    staleTime: 5 * 60 * 1000,
  })
  const ownerSegments = isOwner && mine?.configured ? (mine.segments || []) : []
  let scopeCleared = false
  try { scopeCleared = sessionStorage.getItem(ownerScopeClearedKey(ranchId)) === '1' } catch { /* private mode */ }
  const defaultSegmentId = ownerSegments.length === 1 && ownerSegments[0].id && !scopeCleared ? ownerSegments[0].id : ''
  const effectiveSegmentId = ownerSegmentId || defaultSegmentId
  useEffect(() => {
    if (!defaultSegmentId || searchParams.has('owner_segment_id')) return
    const next = new URLSearchParams(searchParams)
    next.set('owner_segment_id', defaultSegmentId)
    setSearchParams(next, { replace: true })
  }, [defaultSegmentId, searchParams, setSearchParams])

  const [statusFilter, setStatusFilter] = useState('active')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [segmentFilter, setSegmentFilter] = useState('')
  const [herdFilter, setHerdFilter] = useState('')
  const [view, setView] = useState(() => {
    try {
      const v = localStorage.getItem(VIEW_KEY)
      if (v === 'list' || v === 'wall') return v
    } catch { /* private mode */ }
    return isOwner ? 'wall' : 'list'
  })
  const chooseView = (v) => {
    setView(v)
    try { localStorage.setItem(VIEW_KEY, v) } catch { /* private mode */ }
  }

  // RFID scan modal state
  const [scanModalOpen, setScanModalOpen] = useState(false)
  const [scanMode, setScanMode] = useState('lookup') // 'lookup' for events, 'register' for new animals

  const handleAnimalFound = (animal) => {
    setScanModalOpen(false)
    navigate(`/cattle/${animal.id}/event`, { state: { animal } })
  }
  const handleNewRfid = (rfidTag) => {
    setScanModalOpen(false)
    navigate('/cattle/new', { state: { scannedRfid: rfidTag } })
  }
  const openEventScan = () => { setScanMode('lookup'); setScanModalOpen(true) }
  const openRegisterScan = () => { setScanMode('register'); setScanModalOpen(true) }

  // Fetch cattle: 60 per page, the next page as the reader nears the end (plan
  // B1); every filter is part of the key, so a filter change starts over.
  // The Starred filter lists the stars whatever their status (a starred animal
  // that was sold is still findable) and sends at most the API's cap of ids.
  const filters = {
    status: (starred ? '' : statusFilter) || undefined,
    category: categoryFilter || undefined,
    owner_segment: segmentFilter || undefined,
    owner_segment_id: effectiveSegmentId || undefined,
    smartkraal_status: skStatus || undefined,
    seen: seen || undefined,
    herd: herdFilter || undefined,
    search: searchTerm || undefined,
    ids: starred && starredIds.length ? starredIds.slice(0, MAX_STARRED_IDS).join(',') : undefined,
  }
  const { data, isPending: listPending, error, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['cattle', ranchId, 'pages', filters],
    queryFn: ({ pageParam = 0, signal }) => getCattle(ranchId, { ...filters, limit: PAGE, offset: pageParam }, { signal }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((n, p) => n + (p.items?.length || 0), 0)
      return (lastPage.items?.length || 0) === PAGE && loaded < (lastPage.total || 0) ? loaded : undefined
    },
    // an owner's list waits for their segment link to settle (success or error);
    // the Starred filter with no stars asks the server for nothing
    enabled: !(isOwner && !!ranchId && minePending) && !starredEmpty,
  })

  // Herd summary (whole-herd aggregate) — see note below. Kept as a separate
  // parallel query: list is paginated, summary aggregates the whole herd.
  const { data: summary, isPending: summaryPending } = useQuery({
    queryKey: ['herdSummary', ranchId],
    queryFn: ({ signal }) => getHerdSummary(ranchId, { signal }),
    staleTime: 1000 * 60 * 10,
    placeholderData: (prev) => prev,
  })

  const cattle = useMemo(() => (data?.pages || []).flatMap((p) => p.items || []), [data])
  const totalCount = data?.pages?.[0]?.total || 0

  // The sentinel under the results asks for the next page as it scrolls into
  // view; the "Load more" button is the fallback (no IntersectionObserver, or a
  // page short enough that the sentinel never leaves the viewport).
  const sentinelRef = useRef(null)
  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !hasNextPage || typeof IntersectionObserver === 'undefined') return undefined
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) fetchNextPage()
    }, { rootMargin: '800px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, cattle.length, view])

  const byCategory = summary?.by_category || {}
  const bySegment = summary?.by_segment || []
  const totalActive = summary?.active_count
    || Object.values(byCategory).reduce((a, b) => a + b, 0)
  const composition = CATEGORY_META.filter((c) => (byCategory[c.key] || 0) > 0)
  const maxSegment = Math.max(1, ...bySegment.map((s) => s.count))

  const setCategory = (key) => {
    setCategoryFilter((prev) => (prev === key ? '' : key))
  }
  const setSegment = (name) => {
    setSegmentFilter((prev) => (prev.toLowerCase() === (name || '').toLowerCase() ? '' : (name || '')))
    if (ownerSegmentId) clearOwnerSegment()   // one owner scope at a time: the name chip replaces the URL's
  }

  const getCategoryColor = (category) => ({
    calf: 'bg-yellow-100 text-yellow-800',
    weaner: 'bg-orange-100 text-orange-800',
    heifer: 'bg-pink-100 text-pink-800',
    cow: 'bg-purple-100 text-purple-800',
    bull: 'bg-blue-100 text-blue-800',
    steer: 'bg-green-100 text-green-800',
    oxen: 'bg-gray-100 text-gray-800',
  }[category] || 'bg-gray-100 text-gray-800')

  const getHerdColor = (herd) => ({
    Main: 'bg-blue-500', A: 'bg-green-500', B: 'bg-purple-500', C: 'bg-orange-500',
  }[herd] || 'bg-gray-500')

  const getCategoryBorderColor = (category) => ({
    cow: 'border-l-purple-400',
    bull: 'border-l-blue-400',
    heifer: 'border-l-pink-400',
    calf: 'border-l-yellow-400',
    steer: 'border-l-green-400',
    weaner: 'border-l-orange-400',
    oxen: 'border-l-gray-400',
  }[category] || 'border-l-gray-200')

  const kraalConfigured = !!regCounts?.configured

  return (
    <div>
      <RfidScanModal
        isOpen={scanModalOpen}
        onClose={() => setScanModalOpen(false)}
        onAnimalFound={handleAnimalFound}
        onNewRfid={handleNewRfid}
        mode={scanMode}
        ranchId={ranchId}
      />

      <PageHeader
        title="Cattle Management"
        subtitle={`${totalCount} animals${statusFilter ? ` (${statusFilter})` : ''}`}
        icon={Beef}
        actions={canWrite ? (
          <>
            <Link to="/cattle/bulk-treatment" className="btn bg-teal-600 text-white hover:bg-teal-700">
              <Syringe className="h-4 w-4 mr-2" />
              Bulk Treatment
            </Link>
            <Link to="/cattle/photo-upload" className="btn bg-purple-600 text-white hover:bg-purple-700">
              <Camera className="h-4 w-4 mr-2" />
              Upload Photos
            </Link>
            <Link to="/cattle/weigh-session" className="btn bg-amber-600 text-white hover:bg-amber-700">
              <Scale className="h-4 w-4 mr-2" />
              Import Weights
            </Link>
            <Link to="/cattle/smartkraal" className="btn bg-earth-100 text-earth-700 hover:bg-earth-200">
              <Scale className="h-4 w-4 mr-2" />
              SmartKraal
            </Link>
            {regCounts?.configured && (
              <Link to="/cattle/registration" className="btn bg-indigo-600 text-white hover:bg-indigo-700" title="Bind SmartKraal tags to animals">
                <Tag className="h-4 w-4 mr-2" />
                Register at crush
                {regCounts.unbound > 0 && (
                  <span className="ml-2 rounded-full bg-white/90 text-indigo-800 text-xs font-semibold px-2 py-0.5">{regCounts.unbound}</span>
                )}
              </Link>
            )}
            <Link to="/cattle/preg-campaign" className="btn bg-pink-600 text-white hover:bg-pink-700">
              <Baby className="h-4 w-4 mr-2" />
              Preg Test
            </Link>
            <button onClick={openEventScan} className="btn bg-blue-600 text-white hover:bg-blue-700">
              <Radio className="h-4 w-4 mr-2" />
              Enter Event
            </button>
            <div className="flex items-center">
              <button onClick={openRegisterScan} className="btn bg-green-600 text-white hover:bg-green-700 rounded-r-none border-r border-green-500" title="Scan EID">
                <Radio className="h-4 w-4" />
              </button>
              <Link to="/cattle/new" className="btn bg-green-600 text-white hover:bg-green-700 rounded-l-none">
                <Plus className="h-4 w-4 mr-2" />
                Register
              </Link>
            </div>
          </>
        ) : null}
      />

      {/* Herd composition + owner segments. While the summary loads, two cards of
          the same size hold the place so the filters and the wall do not jump
          (Lighthouse CLS on the list route, plan B1). */}
      {summaryPending ? (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6" aria-busy="true" data-testid="summary-placeholder">
          <div className="section-card p-5 lg:col-span-3 min-h-[13.5rem] animate-pulse" />
          <div className="section-card p-5 lg:col-span-2 min-h-[13.5rem] animate-pulse" />
        </div>
      ) : summary && totalActive > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
          {/* Composition */}
          <div className="section-card p-5 lg:col-span-3">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Herd Composition
              </h3>
              <div>
                <span className="text-2xl font-bold tracking-tight text-gray-900">
                  {totalActive.toLocaleString()}
                </span>
                <span className="text-sm text-earth-500 ml-1.5">active</span>
              </div>
            </div>

            {/* Stacked bar */}
            <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100">
              {composition.map((c) => (
                <div
                  key={c.key}
                  className={`${c.bar} transition-all`}
                  style={{ width: `${(byCategory[c.key] / totalActive) * 100}%` }}
                  title={`${c.label}: ${byCategory[c.key].toLocaleString()}`}
                />
              ))}
            </div>

            {/* Legend — click to filter the list by category */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-2.5">
              {composition.map((c) => {
                const active = categoryFilter === c.key
                return (
                  <button
                    key={c.key}
                    onClick={() => setCategory(c.key)}
                    className={`flex items-center gap-2 text-sm rounded-lg px-2 py-1 -mx-2 transition-colors hover:bg-gray-50 ${active ? 'bg-gray-100 ring-1 ring-gray-300' : ''}`}
                    title={`Filter by ${c.label}`}
                  >
                    <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${c.dot}`} />
                    <span className="text-gray-600">{c.label}</span>
                    <span className="ml-auto font-semibold text-gray-900 tabular-nums">
                      {byCategory[c.key].toLocaleString()}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Owner segments */}
          <div className="section-card p-5 lg:col-span-2">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
              By Owner Segment
            </h3>
            {bySegment.length === 0 ? (
              <p className="text-sm text-earth-400">No owner segments yet.</p>
            ) : (
              <div className="space-y-2.5 max-h-56 overflow-y-auto pr-1">
                {bySegment.map((s) => {
                  const name = s.name || 'Unassigned'
                  const pct = totalActive ? Math.round((s.count / totalActive) * 100) : 0
                  const active = segmentFilter.toLowerCase() === (s.name || '').toLowerCase() && !!s.name
                  return (
                    <button
                      key={name}
                      onClick={() => s.name && setSegment(s.name)}
                      disabled={!s.name}
                      className={`w-full text-left rounded-lg px-2 py-1.5 -mx-2 transition-colors ${s.name ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'} ${active ? 'bg-gray-100 ring-1 ring-gray-300' : ''}`}
                      title={s.name ? `Show ${name}'s animals` : undefined}
                    >
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="flex items-center gap-1.5 font-medium text-gray-700 truncate">
                          {s.is_institutional
                            ? <Landmark className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                            : <User className="h-3.5 w-3.5 text-primary-500 flex-shrink-0" />}
                          <span className="truncate">{name}</span>
                        </span>
                        <span className="text-earth-500 flex-shrink-0 ml-2 tabular-nums">
                          <span className="font-semibold text-gray-900">{s.count.toLocaleString()}</span>
                          <span className="text-earth-400"> · {pct}%</span>
                        </span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className={`h-2 rounded-full ${s.is_institutional ? 'bg-amber-400' : 'bg-primary-500'}`}
                          style={{ width: `${(s.count / maxSegment) * 100}%` }}
                        />
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* The two chip rows below appear once the ranch is known to have the kraal
          feed; until then an empty band of their height keeps the page still. */}
      {regPending && !!ranchId && !skStatus && !ownerSegmentId && !seen && (
        <div className="min-h-[5.25rem]" aria-busy="true" data-testid="chips-placeholder" />
      )}

      {/* SmartKraal binding filter (Phase 4a): the owner's dashboard card links here */}
      {(kraalConfigured || skStatus || ownerSegmentId) && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="sk-status-filter">
          <span className="text-xs font-semibold text-earth-500 uppercase tracking-wide mr-1">SmartKraal tag</span>
          {['unbound', 'bound', 'confirmed', 'sent_back'].map((key) => {
            const active = skStatus === key
            return (
              <button key={key} type="button" onClick={() => setSkStatus(key)} aria-pressed={active}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${BINDING_STATUS[key].cls} ${active ? 'ring-2 ring-offset-1 ring-primary-400' : 'opacity-80 hover:opacity-100'}`}>
                {BINDING_STATUS[key].label}
              </button>
            )
          })}
          {ownerSegmentId && (
            <button type="button" onClick={clearOwnerSegment} title="Showing one owner's animals; click to show every owner"
              className="ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-100"
              data-testid="owner-scope-chip">
              <User className="h-3 w-3" /> One owner&apos;s animals <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}

      {/* Kraal presence filter (plan B1): who the kraal has seen, and when */}
      {(kraalConfigured || seen) && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="seen-filter">
          <span className="text-xs font-semibold text-earth-500 uppercase tracking-wide mr-1">At the kraal</span>
          {SEEN_FILTERS.map((f) => {
            const active = seen === f.key
            return (
              <button key={f.key} type="button" onClick={() => setSeen(f.key)} aria-pressed={active} title={f.title}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${active
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-earth-700 border-earth-200 hover:bg-earth-50'}`}>
                {f.label}
              </button>
            )
          })}
          <Link to="/cattle/feed" className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                title="Who the kraal saw, day by day" data-testid="feed-link">
            <CalendarDays className="h-3.5 w-3.5" /> This week at the kraal <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}

      {/* Filters */}
      <div className="section-card p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by tag or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <select
            value={starred ? '' : statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            disabled={starred}
            title={starred ? 'Starred animals are listed whatever their status' : undefined}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:opacity-60"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="sold">Sold</option>
            <option value="deceased">Deceased</option>
            <option value="missing">Missing</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="">All Categories</option>
            <option value="calf">Calf</option>
            <option value="weaner">Weaner</option>
            <option value="heifer">Heifer</option>
            <option value="cow">Cow</option>
            <option value="bull">Bull</option>
            <option value="steer">Steer</option>
            <option value="oxen">Oxen</option>
          </select>

          {/* Segment filter — options come from the live segment breakdown */}
          <select
            value={segmentFilter}
            onChange={(e) => { setSegmentFilter(e.target.value); if (ownerSegmentId) clearOwnerSegment() }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="">All Segments</option>
            {bySegment.filter((s) => s.name).map((s) => (
              <option key={s.name} value={s.name}>{s.name}</option>
            ))}
          </select>

          <select
            value={herdFilter}
            onChange={(e) => setHerdFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="">All Herds</option>
            <option value="Main">Main Herd</option>
            <option value="A">Herd A</option>
            <option value="B">Herd B</option>
            <option value="C">Herd C</option>
          </select>

          {/* List | Wall */}
          <div className="inline-flex self-start rounded-lg border border-gray-300 overflow-hidden" role="group" aria-label="View">
            <button type="button" onClick={() => chooseView('list')} aria-pressed={view === 'list'} title="List"
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm ${view === 'list' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              data-testid="view-list">
              <List className="h-4 w-4" /> List
            </button>
            <button type="button" onClick={() => chooseView('wall')} aria-pressed={view === 'wall'} title="Wall of photos"
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-l border-gray-300 ${view === 'wall' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              data-testid="view-wall">
              <LayoutGrid className="h-4 w-4" /> Wall
            </button>
          </div>

          {/* Starred (plan B2): the animals starred on this device */}
          <button type="button" onClick={toggleStarred} aria-pressed={starred} data-testid="starred-filter"
            title={starred ? 'Showing starred animals; click to show all' : 'Show only the animals you starred'}
            className={`inline-flex items-center gap-1.5 self-start rounded-lg border px-3 py-2 text-sm transition-colors ${starred
              ? 'border-amber-400 bg-amber-50 text-amber-900'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}>
            <Star className={`h-4 w-4 ${starred || starredIds.length ? 'fill-amber-400 text-amber-400' : 'text-gray-400'}`} />
            Starred{starredIds.length ? ` (${starredIds.length})` : ''}
          </button>
        </div>

        {/* Active filter pills (quick-clear) */}
        {(categoryFilter || segmentFilter) && (
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className="text-xs text-earth-400">Filtered:</span>
            {categoryFilter && (
              <button
                onClick={() => setCategoryFilter('')}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-xs text-gray-700 hover:bg-gray-200"
              >
                {categoryFilter}<X className="h-3 w-3" />
              </button>
            )}
            {segmentFilter && (
              <button
                onClick={() => setSegmentFilter('')}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary-50 text-xs text-primary-700 hover:bg-primary-100"
              >
                <User className="h-3 w-3" />{segmentFilter}<X className="h-3 w-3" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Cattle List / Wall */}
      {starredEmpty ? (
        <div className="text-center py-12 bg-white rounded-lg shadow" data-testid="starred-empty">
          <Star className="h-12 w-12 mx-auto text-amber-300 mb-4" />
          <p className="text-gray-700">No starred animals yet on this device.</p>
          <p className="text-sm text-gray-500 mt-1">Tap the star on a photo in the wall, in the kraal feed or on an animal&apos;s page.</p>
          <button type="button" onClick={toggleStarred} className="mt-4 text-sm text-primary-700 hover:underline">Show all animals</button>
        </div>
      ) : listPending ? (
        <div className="text-center py-12">Loading cattle...</div>
      ) : error ? (
        <div className="text-center py-12 text-red-600">Error loading cattle</div>
      ) : cattle.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Beef className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No cattle found</p>
          {canWrite && (
            <div className="flex flex-wrap justify-center gap-3 mt-4">
              {regCounts?.configured && (
                <Link
                  to="/cattle/registration?tab=tagging"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  title="Census mode: register each animal at the race as it is tagged"
                  data-testid="register-herd-at-crush"
                >
                  <Tag className="h-5 w-5" />
                  Register the herd at the crush
                </Link>
              )}
              <Link
                to="/cattle/new"
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus className="h-5 w-5" />
                Register First Animal
              </Link>
            </div>
          )}
        </div>
      ) : view === 'wall' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="cattle-wall">
          {cattle.map((animal) => (
            <WallTile key={animal.id} animal={animal} showPresence={kraalConfigured} ranchId={ranchId} />
          ))}
        </div>
      ) : (
        <div className="section-card">
          <ul className="divide-y divide-gray-200">
            {cattle.map((animal) => {
              const weight = weightCell(animal)
              return (
              <li key={animal.id} className={`hover:bg-gray-50 border-l-4 ${getCategoryBorderColor(animal.category)}`}>
                <div className="flex items-center p-4">
                  <Link to={`/cattle/${animal.id}`} className="flex-1 flex items-center min-w-0">
                    {/* Photo or placeholder */}
                    <Avatar animal={animal} />

                    {/* Details */}
                    <div className="ml-4 flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 truncate">
                          {animal.local_name || animal.rfid_tag}
                        </span>
                        {animal.herd && (
                          <span className={`w-2 h-2 rounded-full ${getHerdColor(animal.herd)}`} title={`Herd ${animal.herd}`} />
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(animal.category)}`}>
                          {animal.category}
                        </span>
                        {animal.owner_segment && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-earth-50 text-earth-600 border border-earth-100">
                            <User className="h-3 w-3" />
                            {animal.owner_segment}
                          </span>
                        )}
                        <span className="truncate">{animal.rfid_tag}</span>
                        {animal.smartkraal_p_idtag && (
                          <BindingChip pIdtag={animal.smartkraal_p_idtag} status={animal.smartkraal_link_status} compact />
                        )}
                        {kraalConfigured && <PresenceChip animal={animal} />}
                        {animal.breed_name && (
                          <>
                            <span className="hidden sm:inline">•</span>
                            <span className="hidden sm:inline truncate">{animal.breed_name}</span>
                          </>
                        )}
                        {animal.birth_date && (
                          <>
                            <span className="hidden sm:inline">•</span>
                            <span className="hidden sm:inline">{calculateAge(animal.birth_date)}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Weight */}
                    <div className="hidden md:block text-right mr-4">
                      {weight && (
                        <div className="text-sm font-medium text-gray-900" title={weight.title}>
                          {formatKg(weight.kg)} kg
                        </div>
                      )}
                      {weight?.delta && <DeltaBadge delta={weight.delta} />}
                    </div>

                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </Link>

                  {/* Quick Action Buttons */}
                  {canWrite && (
                  <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-200">
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        navigate(`/cattle/${animal.id}/event`, { state: { animal, defaultType: 'weighing' } })
                      }}
                      className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Record Weight"
                    >
                      <Scale className="h-5 w-5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        navigate(`/cattle/${animal.id}/event`, { state: { animal, defaultType: 'treatment' } })
                      }}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Record Treatment"
                    >
                      <Syringe className="h-5 w-5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        navigate(`/cattle/${animal.id}/event`, { state: { animal } })
                      }}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                    >
                      + Event
                    </button>
                  </div>
                  )}
                </div>
              </li>
              )
            })}
          </ul>
        </div>
      )}

      {/* Footer: how far the reader is, and the next page as they get there */}
      {!listPending && !error && cattle.length > 0 && (
        <div className="flex flex-col items-center gap-2 py-4 text-sm text-gray-500" data-testid="list-footer">
          <div ref={sentinelRef} aria-hidden="true" />
          <span>Showing {cattle.length.toLocaleString()} of {totalCount.toLocaleString()}</span>
          {hasNextPage && (
            <button type="button" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}
              className="px-3 py-1 border border-gray-300 rounded-lg bg-white disabled:opacity-50 hover:bg-gray-50"
              data-testid="load-more">
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/** The list row's avatar: the display photo's thumb (hero), else the stored
 *  photo, else the beef glyph. A thumb that is not built yet shows the glyph. */
function Avatar({ animal }) {
  const src = animal.hero?.thumb || (animal.photo_url ? variantUrl(animal.photo_url, 'thumb') : null)
  return (
    <MediaThumb src={src} alt={animal.local_name || animal.rfid_tag}
                className="h-12 w-12 rounded-full bg-green-100 ring-1 ring-green-200 flex-shrink-0"
                glyph={<Beef className="h-6 w-6 text-green-600" />} />
  )
}

/**
 * One wall tile (plan B1): the hero thumb in a 4:3 box over the thumb's own
 * colour while it loads (a silhouette when there is no photo or the thumb is
 * not built yet; never the 3 MB original in a grid), the presence chip, name
 * and SmartKraal tag, the weight of record with its change against the
 * previous kraal weighing, the owner, and the star (plan B2).
 */
function WallTile({ animal, showPresence, ranchId }) {
  const weight = weightCell(animal)
  const hero = animal.hero
  const thumb = hero?.thumb || (animal.photo_url ? variantUrl(animal.photo_url, 'thumb') : null)
  const name = animal.local_name || animal.rfid_tag
  return (
    <div className="relative" data-testid="wall-tile">
    <Link to={`/cattle/${animal.id}`}
      className="group block overflow-hidden rounded-xl border border-earth-200 bg-white shadow-sm transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500">
      <div className="relative">
        <MediaThumb src={thumb} className="aspect-[4/3] w-full" style={{ backgroundColor: hero?.dominant_rgb || '#e8e2d6' }}
                    glyph={<Beef className="h-10 w-10 text-black/20" aria-hidden="true" />} />
        {showPresence && <PresenceChip animal={animal} className="absolute left-1.5 top-1.5 px-2 py-0.5 text-[11px] shadow-sm" />}
      </div>
      <div className="p-2.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate font-medium text-gray-900" title={name}>{name}</span>
          {animal.smartkraal_p_idtag && (
            <span className="shrink-0 text-xs text-earth-500" title="SmartKraal tag">Tag {animal.smartkraal_p_idtag}</span>
          )}
        </div>
        <div className="mt-1 flex items-center justify-between gap-2 text-xs text-earth-600">
          <span className="flex items-center gap-1.5 shrink-0" title={weight?.title}>
            {weight
              ? <span className="font-medium text-gray-900">{formatKg(weight.kg)} kg</span>
              : <span className="text-earth-400">no weight</span>}
            {weight?.delta && <DeltaBadge delta={weight.delta} />}
          </span>
          {animal.owner_segment && (
            <span className="truncate" title={animal.owner_segment}>{animal.owner_segment}</span>
          )}
        </div>
      </div>
    </Link>
    {/* the star sits beside the link, never inside it (no button inside an anchor) */}
    <StarButton ranchId={ranchId} animalId={animal.id} name={name} className="absolute right-1 top-1" />
    </div>
  )
}

// Helper function to calculate age from an ISO date string
function calculateAge(dateOfBirth) {
  const birth = new Date(dateOfBirth)
  const now = new Date()
  const months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth())

  if (months < 1) {
    const days = Math.floor((now - birth) / (1000 * 60 * 60 * 24))
    return `${days} days`
  } else if (months < 12) {
    return `${months} months`
  } else {
    const years = Math.floor(months / 12)
    const remainingMonths = months % 12
    if (remainingMonths === 0) {
      return `${years} year${years > 1 ? 's' : ''}`
    }
    return `${years}y ${remainingMonths}m`
  }
}
